import React, { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import ThgPricesButton from "../ThgPrices/ThgPricesButton";
import ReportingOptionsBar from "./components/ReportingOptionsBar";
import PerformanceDashboard from "./components/PerformanceDashboard";
import StationLeagueTable from "./components/StationLeagueTable";
import LifetimeLeagueTable from "./components/LifetimeLeagueTable";
import StationDetail from "./components/StationDetail";
import UnderperformanceWatchlist from "./components/UnderperformanceWatchlist";
import FleetDistribution from "./components/FleetDistribution";
import BackfillPanel from "./components/BackfillPanel";
import { useReportingOptions } from "./hooks/useReportingOptions";
import { useSelectedMonth } from "./hooks/useSelectedMonth";

/** `YYYY-MM` -> first of that month (local), for the Calendar. */
const toMonthDate = (yearMonth: string) => {
  const [y, m] = yearMonth.split("-").map(Number);
  return new Date(y, m - 1, 1);
};

/** `YYYY-MM` for a Date, in local terms: the API's month keys. */
const toYearMonth = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

/**
 * Named so a drill-through cannot land on the wrong tab. Every one of these opens Station detail, and
 * the previous numeric literals silently pointed at the wrong panel the moment a tab was inserted.
 */
const TAB = {
  DASHBOARD: 0,
  STATION_LIST: 1,
  LIFETIME: 2,
  STATION_DETAIL: 3,
  WATCHLIST: 4,
  VARIANCE: 5,
  BACKFILL: 6,
} as const;

/**
 * `?tab=backfill` opens a tab directly. BOF-76: the charging station dashboard lives under Analytics and
 * sends a reader here when a period was never computed, because the recompute that fills it is this
 * page's backfill.
 */
const TAB_BY_PARAM: Record<string, number> = { backfill: TAB.BACKFILL };

/**
 * CP-388 station performance analytics.
 *
 * Six views over one set of precomputed aggregates. The Dashboard answers "are we making more money
 * than last month" for anyone who walks up to it; the Station list carries the full per-station detail
 * for the people who came to read numbers. Splitting them keeps the first screen legible without
 * taking anything away from the second. They share the reporting options deliberately: a
 * comparison begun per connector must not silently continue per site when the reader switches tab
 * (FR-030a).
 *
 * The variance tab cannot honour a channel filter, so the control is disabled there rather than being
 * silently ignored: the stored per-channel breakdown has counters only, and a per-channel spread cannot
 * be derived from it.
 */
const StationPerformance: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { options, setOptions } = useReportingOptions();
  const [searchParams] = useSearchParams();
  const [activeIndex, setActiveIndex] = useState(() => TAB_BY_PARAM[searchParams.get("tab") ?? ""] ?? TAB.DASHBOARD);
  const { month, setMonth, reset: resetMonth, currentMonth, earliestMonth, isCurrentMonth } = useSelectedMonth();
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  /**
   * CLR-296 bumped after a THG price changed. Each tab fetches in its own hook, so remounting the tabs
   * is how every figure is re-read; the selected tab, month and station live here and survive.
   */
  const [reloadKey, setReloadKey] = useState(0);
  const reloadFigures = useCallback(() => setReloadKey((k) => k + 1), []);

  /** Every drill-through in every tab does the same two things, so it is defined once. */
  const openStationDetail = useCallback((unitId: string) => {
    setSelectedUnitId(unitId);
    setActiveIndex(TAB.STATION_DETAIL);
  }, []);

  const isDashboardTab = activeIndex === TAB.DASHBOARD;
  const isBackfillTab = activeIndex === TAB.BACKFILL;
  const isVarianceTab = activeIndex === TAB.VARIANCE;

  return (
    <div className="layout-main">
      <Toast ref={toast} />

      <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
        <div>
          <h2 className="m-0">Station performance</h2>
          <span className="text-color-secondary text-sm">
            Sessions, energy, money and free share per station — with month-over-month change, lifetime
            rankings and variance.
          </span>
        </div>
        <ThgPricesButton onSaved={reloadFigures} />
        {/* Hidden only on Data & backfill, which recomputes ranges rather than reading a month. */}
        {!isBackfillTab && (
          <div className="flex flex-column gap-1">
            <label className="text-sm text-color-secondary">Month</label>
            <Calendar
              value={toMonthDate(month)}
              onChange={(e) => e.value && setMonth(toYearMonth(e.value as Date))}
              minDate={earliestMonth ? toMonthDate(earliestMonth) : undefined}
              maxDate={toMonthDate(currentMonth)}
              view="month"
              dateFormat="yy-mm"
              showIcon
            />
          </div>
        )}
      </div>

      {/* The lenses describe how to read figures; a recompute does not read any, so the bar is hidden
          there rather than shown doing nothing. */}
      {!isBackfillTab && (
        <ReportingOptionsBar
          options={options}
          onChange={setOptions}
          providerFilterDisabled={isVarianceTab}
          moneyViewDisabled={isDashboardTab}
          onReset={resetMonth}
        />
      )}

      <TabView key={reloadKey} activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header="Dashboard" leftIcon="pi pi-th-large mr-2">
          <PerformanceDashboard month={month} options={options} onSelectUnit={openStationDetail} />
        </TabPanel>

        <TabPanel header="Station list" leftIcon="pi pi-list mr-2">
          <StationLeagueTable month={month} options={options} onSelectUnit={openStationDetail} />
        </TabPanel>

        <TabPanel header="Lifetime" leftIcon="pi pi-trophy mr-2">
          <LifetimeLeagueTable asOf={month} options={options} onSelectUnit={openStationDetail} />
        </TabPanel>

        <TabPanel header="Station detail" leftIcon="pi pi-chart-line mr-2">
          <StationDetail unitId={selectedUnitId} month={month} options={options} />
        </TabPanel>

        <TabPanel header="Watchlist" leftIcon="pi pi-exclamation-triangle mr-2">
          <UnderperformanceWatchlist
            options={options}
            month={month}
            isCurrentMonth={isCurrentMonth}
            onSelectUnit={openStationDetail}
          />
        </TabPanel>

        <TabPanel header="Fleet variance" leftIcon="pi pi-sliders-h mr-2">
          <FleetDistribution month={month} options={options} onSelectUnit={openStationDetail} />
        </TabPanel>

        <TabPanel header="Data & backfill" leftIcon="pi pi-database mr-2">
          <BackfillPanel />
        </TabPanel>
      </TabView>
    </div>
  );
};

export default StationPerformance;
